import { useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEditorWebsocket, useCheckEstimateEditorAccess } from "@hooks";
import {
  useEstimateEditorStore,
  Share,
  EstimateSettings,
  CommentsDrawer,
  useEstimateComments,
  useEstimateEditorSettings
} from "@tools";
import { CreateTemplateDialog } from "@dialogs";
import { ActionButton, Alert, EstimateNameEditorV2 } from "@components";
import { Grid } from "@material-ui/core";
import { Exports, Imports, ProfitabilityMode, VisibilityMode } from "./components";
import { FileAdd } from "@assets";
import useStyle from "./TableToolbar.style";
import classnames from "classnames";

export const ProjectTableToolbar = observer(({
  isSharedVersion,
  hideCommentSidebar,
}) => {
  const { t } = useTranslation();
  const { estimateUuid } = useParams();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const { allVisibleComments } = useEstimateComments();
  const { isPdfPrinterView } = useEstimateEditorSettings();
  const socket = useEditorWebsocket();
  const classes = useStyle();

  const [ alertText, setAlertText ] = useState("");
  const [ showTemplateDialog, openTemplateDialog ] = useState(false);

  const {
    estimateName,
    estimateDesc,
    estimateUsers,
    hideExports,
  } = editorStore;
  
  const { isBusinessUser, isObserver, isOwner, isMiniUser } = useCheckEstimateEditorAccess(estimateUsers);

  const changeProjectName = async (newName) => {
    if (estimateName !== newName) {
      await socket?.requestEstimateRename?.(estimateUuid, newName);
      editorStore.setEstimateName(newName);
    }
  };
  
  const handleCustomWorkTypesSave = async () => {
    const customWorkTypes = editorStore.metadataWorkTypes
      .filter(wT => {
        const f = editorStore.workTypes.find(sWT => wT.id === sWT.id);
        return !f || f.custom;
      })
      .map(({ id, name, color }) => ({ oldId: id, name, color }));
    
    if(customWorkTypes.length && socket)
      await socket.requestVersionWorkTypesChange(customWorkTypes);
  }

  return (
    <Grid
      item container
      justifyContent="space-between"
      alignItems="flex-start"
      wrap="nowrap"
      name="table_title"
      className={classnames(classes.container, "gap-3", isSharedVersion && !isPdfPrinterView && "mb-5")}
    >
      <Grid
        item container
        alignItems="center"
        className="w-max max-w-full"
      >
        <EstimateNameEditorV2
          name={estimateName}
          onSubmit={changeProjectName}
          allowEdition={!isSharedVersion && !isMiniUser && isOwner}
          useH1
        />
        {
          isSharedVersion && !isPdfPrinterView && !hideCommentSidebar &&
          <Grid
            item container
            justifyContent="flex-end"
            className={classnames(classes.actionContainer, "ml-3")}
          >
            {
              Boolean(allVisibleComments?.length) &&
              <CommentsDrawer size="small" />
            }
            {
              !hideExports &&
              <Exports size="small" sharedExport />
            }
          </Grid>
        }
      </Grid>
      {
        !isSharedVersion &&
        <Grid
          item container
          justifyContent="flex-end"
          wrap="nowrap"
          className={classes.actionContainer}
        >
          { isBusinessUser && isObserver && <VisibilityMode /> }
          { isBusinessUser && isObserver && <ProfitabilityMode /> }
          { isBusinessUser && isObserver && <EstimateSettings /> }
          { isOwner && <>
            <ActionButton
              square
              icon={<FileAdd />}
              color="secondary"
              onClick={() => openTemplateDialog(true)}
              name={t("views.project.project_template.save_as")}
              tooltip
            />
            {
              showTemplateDialog &&
              <CreateTemplateDialog
                projectUuid={estimateUuid}
                open={showTemplateDialog}
                onBeforeSave={handleCustomWorkTypesSave}
                onClose={() => openTemplateDialog(false)}
                hasComments={commentStore.allVisibleComments.length > 0}
                defaultValues={{
                  description: estimateDesc,
                  name: estimateName
                }}
              />
            }
          </>}
          { isObserver && <Imports /> }
          <Exports />
          <CommentsDrawer />
          { isBusinessUser && <Share /> }
          <Alert
            title={alertText}
            isOpen={!!alertText}
            onAccept={() => setAlertText("")}
            acceptText={t("common.close")}
          />
        </Grid>
      }
    </Grid>
  );
});

ProjectTableToolbar.propTypes = {
  isSharedVersion: bool,
  hideCommentSidebar: bool,
};
